import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import MarketingShell from "./MarketingShell";
import { Plus, Trash, ShieldSlash } from "@phosphor-icons/react";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

export default function SuppressionPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ email: "", reason: "manual", note: "" });

  const reload = async () => {
    setLoading(true);
    try { setRows((await api.get("/marketing/suppression")).data); }
    catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const doAdd = async () => {
    if (!form.email) return;
    try {
      await api.post("/marketing/suppression", form);
      toast.success("Email suppressed globally");
      setAddOpen(false); setForm({ email: "", reason: "manual", note: "" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doRemove = async (id) => {
    if (!window.confirm("Remove from suppression? This email can receive marketing emails again.")) return;
    try { await api.delete(`/marketing/suppression/${id}`); toast.success("Removed"); reload(); }
    catch { toast.error("Failed"); }
  };

  const REASON_COLORS = {
    unsubscribe: { bg: "#FEF2F2", fg: "#DC2626" },
    hard_bounce: { bg: "#FEF3C7", fg: "#92400E" },
    complaint: { bg: "#FEF2F2", fg: "#DC2626" },
    manual: { bg: "#F5F5F5", fg: "#525252" },
  };

  return (
    <MarketingShell>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-start gap-2">
          <ShieldSlash size={20} className="text-[#DC2626] mt-0.5" />
          <div>
            <p className="text-sm font-bold text-[#0A0A0A]">Global suppression list</p>
            <p className="text-xs text-[#525252]">Emails here never receive marketing campaigns, regardless of source. Applies across CSV imports, clients, and leads.</p>
          </div>
        </div>
        <button onClick={() => setAddOpen(true)}
                className="h-9 px-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                data-testid="add-suppression-btn">
          <Plus size={12} weight="bold" />Add Email
        </button>
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        {loading ? <p className="p-8 text-center text-sm text-[#A3A3A3]">Loading…</p>
        : rows.length === 0 ? <p className="p-8 text-center text-sm text-[#A3A3A3]">No suppressed emails.</p>
        : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-5 py-2 font-bold">Email</th>
                <th className="text-left px-3 py-2 font-bold">Reason</th>
                <th className="text-left px-3 py-2 font-bold">Note</th>
                <th className="text-left px-3 py-2 font-bold">Added</th>
                <th className="text-right px-5 py-2 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const c = REASON_COLORS[r.reason] || REASON_COLORS.manual;
                return (
                  <tr key={r.id} className="border-t border-[#F5F5F5]" data-testid={`supp-row-${r.id}`}>
                    <td className="px-5 py-3 font-semibold text-[#0A0A0A]">{r.email}</td>
                    <td className="px-3 py-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5" style={{ backgroundColor: c.bg, color: c.fg }}>
                        {r.reason}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-[#525252] max-w-xs truncate">{r.note || "—"}</td>
                    <td className="px-3 py-3 text-xs text-[#A3A3A3]">{formatLocalDate(r.added_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => doRemove(r.id)} className="p-1 text-[#525252] hover:text-[#0055FF]" title="Remove from suppression" data-testid={`unsupp-${r.id}`}>
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {addOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setAddOpen(false)}>
          <div className="bg-white border border-[#E5E5E5] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-black tracking-tight mb-3">Add email to suppression</h3>
            <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com"
                   className="w-full h-10 px-3 border border-[#E5E5E5] mb-3" data-testid="supp-email" autoFocus />
            <select value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
                    className="w-full h-10 px-3 border border-[#E5E5E5] mb-3" data-testid="supp-reason">
              <option value="manual">Manual — added by admin</option>
              <option value="gdpr">GDPR / right-to-be-forgotten</option>
              <option value="complaint">Complaint from recipient</option>
            </select>
            <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                      placeholder="Note (optional)" rows={2}
                      className="w-full px-3 py-2 border border-[#E5E5E5] mb-4" data-testid="supp-note" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setAddOpen(false)} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">Cancel</button>
              <button onClick={doAdd} className="h-9 px-4 bg-[#DC2626] text-white text-xs font-bold uppercase tracking-wider" data-testid="supp-confirm">Add to suppression</button>
            </div>
          </div>
        </div>
      )}
    </MarketingShell>
  );
}
